// Currency formatting with Canadian support
export const formatCurrency = (amount: number, currency: string = 'CAD'): string => {
  // Handle NaN, null, undefined values
  if (amount === null || amount === undefined || isNaN(amount)) {
    amount = 0;
  }
  
  const formatter = new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return formatter.format(amount);
};

// Date formatting for Canadian locale
// CRITICAL FIX: Avoid timezone shifts when parsing date-only strings
export const formatDate = (date: string | Date): string => {
  let dateObj: Date;
  
  if (typeof date === 'string') {
    // Handle ISO timestamp strings (e.g., "2025-12-16T00:00:00.000Z")
    // Extract just the date portion to avoid timezone issues
    const isoMatch = date.match(/^(\d{4})-(\d{1,2})-(\d{1,2})T/);
    if (isoMatch) {
      const [, year, month, day] = isoMatch;
      // Create date in local timezone to avoid UTC shift
      dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    } else {
      // Handle plain YYYY-MM-DD format
      const ymdMatch = date.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
      if (ymdMatch) {
        const [, year, month, day] = ymdMatch;
        // Create date in local timezone to avoid UTC shift
        dateObj = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      } else {
        // For other formats, use Date constructor (may have timezone issues)
        dateObj = new Date(date);
      }
    }
  } else {
    dateObj = date;
  }
  
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(dateObj);
};

export const formatDateForInput = (date: string | Date): string => {
  if (typeof date === 'string') {
    // If already in YYYY-MM-DD format, return as-is
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date;
    }
    // Otherwise parse it
    const dateObj = new Date(date);
    return dateObj.toISOString().split('T')[0];
  }
  return date.toISOString().split('T')[0];
};

// Two-tier category system: High-level categories for insights + Plaid-aligned subtypes for matching

// Mapping from Plaid subtypes to high-level categories
export const getHighLevelCategory = (plaidSubtype: string): string => {
  const mapping: { [key: string]: string } = {
    // Personal Cash equivalents
    'checking': 'Cash',
    'savings': 'Cash',
    'money market': 'Cash',
    'cd': 'Cash',

    // Personal Investment accounts
    'brokerage': 'Investments',
    'ira': 'Investments',
    '401k': 'Investments',
    'rrsp': 'Investments',
    'tfsa': 'Investments',
    'resp': 'Investments',
    'pension': 'Investments',

    // Personal Real assets
    'real estate': 'Real Estate',
    'vehicles': 'Vehicles',

    // Business Cash equivalents (Plaid might use same subtypes)
    'business checking': 'Cash',
    'business savings': 'Cash',
    'business money market': 'Cash',

    // Business Investment accounts
    'business brokerage': 'Investments',
    'business investments': 'Investments',

    // Business Real assets
    'business real estate': 'Real Estate',
    'business vehicles': 'Vehicles',

    // Default
    'other': 'Other Assets'
  };
  return mapping[plaidSubtype.toLowerCase()] || 'Other Assets';
};

// Mapping from existing Wealthini categories to high-level categories
export const getHighLevelCategoryFromWealthiniCategory = (category: string): string => {
  const mapping: { [key: string]: string } = {
    // Personal Cash equivalents
    'checking account': 'Cash',
    'savings account': 'Cash',
    'money market': 'Cash',
    'cd': 'Cash',
    'cash': 'Cash',
    
    // Personal Investment accounts
    'brokerage account': 'Investments',
    'ira': 'Investments',
    '401(k)': 'Investments',
    'rrsp': 'Investments',
    'tfsa': 'Investments',
    'resp': 'Investments',
    'pension': 'Investments',
    'investment accounts': 'Investments',
    
    // Personal Real assets
    'real estate': 'Real Estate',
    'vehicles': 'Vehicles',
    
    // Precious Metals
    'precious metals': 'Precious Metals',
    'gold': 'Precious Metals',
    'silver': 'Precious Metals',
    'platinum': 'Precious Metals',
    'palladium': 'Precious Metals',
    
    // Business Cash equivalents
    'business checking': 'Cash',
    'business savings': 'Cash',
    'business money market': 'Cash',
    
    // Business Investment accounts
    'business investments': 'Investments',
    
    // Business Real assets
    'business real estate': 'Real Estate',
    'business vehicles': 'Vehicles',
    'equipment & machinery': 'Other Assets',
    'inventory': 'Other Assets',
    'accounts receivable': 'Other Assets',
    'intellectual property': 'Other Assets',
    
    // Business Liabilities
    'business credit cards': 'Other Assets',
    'business loans': 'Other Assets',
    'equipment financing': 'Other Assets',
    'business line of credit': 'Other Assets',
    'accounts payable': 'Other Assets',
    'business mortgage': 'Other Assets',
    'tax liabilities': 'Other Assets',
    
    // Default
    'other assets': 'Other Assets',
    'other business assets': 'Other Assets',
    'other business debt': 'Other Assets'
  };
  
  return mapping[category.toLowerCase()] || 'Other Assets';
};

// Plaid-aligned subtypes for account creation and matching
export const getPlaidSubtypes = (): string[] => {
  return [
    'checking', 'savings', 'money market', 'cd',
    'brokerage', 'ira', '401k', 'rrsp', 'tfsa', 'resp', 'pension',
    'real estate', 'vehicles', 'other'
  ];
};

// High-level categories for insights and allocation
export const getHighLevelCategories = (): string[] => {
  return ['Cash', 'Investments', 'Real Estate', 'Vehicles', 'Precious Metals', 'Other Assets'];
};

// Business-specific high-level categories
export const getBusinessHighLevelCategories = (): string[] => {
  return ['Cash', 'Investments', 'Real Estate', 'Vehicles', 'Business Assets', 'Other Assets'];
};

// Canadian-specific asset categories (Plaid-aligned)
export const getAssetCategories = (accountType?: 'personal' | 'business'): string[] => {
  const baseCategories = [
    'Checking Account', 'Savings Account', 'Money Market', 'CD',
    'Brokerage Account', 'IRA', '401(k)', 'RRSP', 'TFSA', 'RESP',
    'Cryptocurrency',
    'Real Estate', 'Vehicles', 'Precious Metals', 'Other Assets'
  ];

  if (accountType === 'business') {
    return [
      'Business Checking', 'Business Savings', 'Business Money Market',
      'Business Investments', 'Business Real Estate', 'Business Vehicles',
      'Equipment & Machinery', 'Inventory', 'Accounts Receivable',
      'Intellectual Property', 'Other Business Assets'
    ];
  }

  if (accountType === 'personal') {
    return [
      ...baseCategories,
      'Personal Property'
    ];
  }

  return baseCategories;
};

// Canadian-specific liability categories (Plaid-aligned)
export const getLiabilityCategories = (accountType?: 'personal' | 'business'): string[] => {
  const baseCategories = [
    'Credit Cards',
    'Mortgage',
    'Auto Loan',
    'Personal Loans',
    'Line of Credit',
    'Student Loans',
    'Other Debt'
  ];

  if (accountType === 'business') {
    return [
      'Business Credit Cards',
      'Business Loans',
      'Equipment Financing',
      'Business Line of Credit',
      'Accounts Payable',
      'Business Mortgage',
      'Tax Liabilities',
      'Other Business Debt'
    ];
  }

  if (accountType === 'personal') {
    return baseCategories;
  }

  return baseCategories;
};

// Personal finance categories for privacy-focused approach
export const getPersonalFinanceCategories = (): string[] => {
  return [
    'Housing',
    'Transportation',
    'Food & Dining',
    'Entertainment',
    'Healthcare',
    'Education',
    'Shopping',
    'Utilities',
    'Insurance',
    'Other Expenses'
  ];
};

// Canadian financial institution categories
export const getInstitutionCategories = (): string[] => {
  return [
    'Big 5 Bank',
    'Credit Union',
    'Digital Bank',
    'Investment Platform',
    'Insurance Company',
    'Other'
  ];
};

// Privacy levels for user control
export const getPrivacyLevels = (): string[] => {
  return [
    'High Privacy (Local Storage)',
    'Standard Privacy (Encrypted Cloud)',
    'Shared Privacy (Business Access)'
  ];
};

// Canadian provinces for location tracking
export const getCanadianProvinces = (): string[] => {
  return [
    'Alberta',
    'British Columbia',
    'Manitoba',
    'New Brunswick',
    'Newfoundland and Labrador',
    'Nova Scotia',
    'Ontario',
    'Prince Edward Island',
    'Quebec',
    'Saskatchewan',
    'Northwest Territories',
    'Nunavut',
    'Yukon'
  ];
}; 

/**
 * Parse currency input from various formats
 * Handles: $1,234.56, 1,234.56, 1234.56, $1234, etc.
 * Robust to Unicode spaces and odd characters from financial institution copy-paste.
 */
export const parseCurrencyInput = (input: string): number => {
  if (!input || typeof input !== 'string') return 0;
  const normalized = input.replace(/\s/g, '').replace(/[$€£¥₹₿,]/g, '').trim();
  if (normalized === '') return 0;
  const match = normalized.match(/-?\d+\.?\d*/);
  if (!match) return 0;
  const parsed = parseFloat(match[0]);
  return isNaN(parsed) ? 0 : parsed;
};

/**
 * Format currency input for display
 * Converts number back to formatted string with commas
 */
export const formatCurrencyInput = (value: number): string => {
  if (value === 0) return '';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}; 